import { type JSX, useEffect, useRef } from 'react';

const SITEKEY = import.meta.env['VITE_TURNSTILE_SITEKEY'] as string | undefined;

/** True when a Turnstile sitekey is configured — the form gates submit on a token. */
export const TURNSTILE_ENABLED = SITEKEY !== undefined && SITEKEY.length > 0;

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  remove: (id: string) => void;
}

/**
 * Cloudflare Turnstile widget. Renders nothing when no sitekey is configured
 * (`VITE_TURNSTILE_SITEKEY` unset) — the form then submits without a token and
 * the server's check is off too, so the pair stays consistent.
 */
export function Turnstile({
  onToken,
}: {
  onToken: (token: string | null) => void;
}): JSX.Element | null {
  const ref = useRef<HTMLDivElement | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!TURNSTILE_ENABLED) return undefined;
    const el = ref.current;
    if (el === null) return undefined;
    let widgetId: string | undefined;
    let cancelled = false;
    void loadScript().then(() => {
      if (cancelled) return;
      const ts = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
      if (ts === undefined) return;
      widgetId = ts.render(el, {
        sitekey: SITEKEY,
        callback: (t: string) => onTokenRef.current(t),
        'expired-callback': () => onTokenRef.current(null),
        'error-callback': () => onTokenRef.current(null),
      });
    });
    return () => {
      cancelled = true;
      const ts = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
      if (ts !== undefined && widgetId !== undefined) ts.remove(widgetId);
    };
  }, []);

  if (!TURNSTILE_ENABLED) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '0.75rem 0' }}>
      <div ref={ref} className="cf-turnstile" data-testid="turnstile" />
    </div>
  );
}

let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (scriptPromise !== null) return scriptPromise;
  scriptPromise = new Promise<void>((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
  return scriptPromise;
}
